import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import {
  createTutorialRequest,
  getTutorials,
  removeTutorial,
  updateTutorialRequest,
} from "../../../../services/api/landing/tutorials/tutorialsApi";

const getLang = () =>
  localStorage.getItem("language") === "ar" ? "ar" : "en";

const toastMessages = {
  add: {
    en: "Tutorial created successfully!",
    ar: "تم إنشاء الدرس بنجاح!",
  },
  addError: {
    en: "Failed to create tutorial.",
    ar: "فشل في إنشاء الدرس.",
  },
  update: {
    en: "Tutorial updated successfully!",
    ar: "تم تحديث الدرس بنجاح!",
  },
  updateError: {
    en: "Failed to update tutorial.",
    ar: "فشل في تحديث الدرس.",
  },
  delete: {
    en: "Tutorial deleted successfully!",
    ar: "تم حذف الدرس بنجاح!",
  },
  deleteError: {
    en: "Failed to delete tutorial.",
    ar: "فشل في حذف الدرس.",
  },
};

export const fetchTutorials = createAsyncThunk(
  "tutorials/fetchTutorials",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getTutorials();
      return response.data.data;
    } catch (error) {
      return rejectWithValue("Failed to fetch tutorials");
    }
  },
);

export const createTutorial = createAsyncThunk(
  "tutorials/createTutorial",
  async (tutorialData, { rejectWithValue }) => {
    try {
      const response = await createTutorialRequest(tutorialData);
      return response.data.data;
    } catch (error) {
      return rejectWithValue("Failed to create tutorial");
    }
  },
);

export const updateTutorial = createAsyncThunk(
  "tutorials/updateTutorial",
  async ({ tutorialId, tutorialData }, { rejectWithValue }) => {
    try {
      const response = await updateTutorialRequest(tutorialId, tutorialData);
      return response.data.data;
    } catch (error) {
      return rejectWithValue("Failed to update tutorial");
    }
  },
);

export const deleteTutorial = createAsyncThunk(
  "tutorials/deleteTutorial",
  async (tutorialId, { rejectWithValue }) => {
    try {
      await removeTutorial(tutorialId);
      return tutorialId;
    } catch (error) {
      return rejectWithValue("Failed to delete tutorial");
    }
  },
);

const tutorialsSlice = createSlice({
  name: "tutorials",
  initialState: {
    tutorialData: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchTutorials.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTutorials.fulfilled, (state, action) => {
        state.loading = false;
        state.tutorialData = action.payload;
      })
      .addCase(fetchTutorials.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createTutorial.fulfilled, (state, action) => {
        state.tutorialData.push(action.payload);
        toast.success(toastMessages.add[getLang()]);
      })
      .addCase(createTutorial.rejected, (state, action) => {
        state.error = action.payload;
        toast.error(toastMessages.addError[getLang()]);
      })
      .addCase(updateTutorial.fulfilled, (state, action) => {
        const index = state.tutorialData.findIndex(
          (tutorial) => tutorial.id === action.payload.id,
        );
        if (index !== -1) {
          state.tutorialData[index] = action.payload;
        }
        toast.success(toastMessages.update[getLang()]);
      })
      .addCase(updateTutorial.rejected, (state, action) => {
        state.error = action.payload;
        toast.error(toastMessages.updateError[getLang()]);
      })
      .addCase(deleteTutorial.fulfilled, (state, action) => {
        state.tutorialData = state.tutorialData.filter(
          (tutorial) => tutorial.id !== action.payload,
        );
        toast.success(toastMessages.delete[getLang()]);
      })
      .addCase(deleteTutorial.rejected, (state, action) => {
        state.error = action.payload;
        toast.error(toastMessages.deleteError[getLang()]);
      });
  },
});

export default tutorialsSlice.reducer;
